import { useEffect, useState } from "react";
import axios from "axios";
import { Coffee, Users, Radio } from "lucide-react";
import { API } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { useT, tDay } from "@/lib/i18n";

export default function NowOnStage() {
  const { t, lang } = useT();
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => axios.get(`${API}/public/now`).then((r) => setD(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <TopBar lang title={t("now.title")} right={
        <span className="flex items-center gap-2 font-mono text-xs text-emerald-400"><span className="h-2 w-2 rounded-full bg-emerald-500 live-dot" />LIVE</span>
      } />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400">FLY CUP 2026{d?.live && ` · ${tDay(d.day, lang)}`}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold uppercase text-emerald-400">{t("now.title")}</h1>
        {d?.hidden && (
          <div data-testid="now-hidden" className="fly-card p-10 mt-8 text-center text-slate-400">
            <Radio className="w-8 h-8 mx-auto mb-3" />{t("now.hidden")}
          </div>
        )}
        {d && !d.live && !d.hidden && (
          <div data-testid="now-empty" className="fly-card p-10 mt-8 text-center text-slate-400">
            <Radio className="w-8 h-8 mx-auto mb-3" />{t("now.empty")}
          </div>
        )}
        {d?.live && d.on_break && <BreakCard d={d} t={t} />}
        {d?.live && !d.on_break && (
          <>
            <section key={d.current.id} data-testid="now-current" className="mt-8 fly-card p-6 sm:p-10 border-emerald-500/50 shadow-[0_0_50px_rgba(16,185,129,0.2)] rise">
              <div className="flex items-center justify-between gap-3 flex-wrap font-mono text-xs uppercase tracking-[0.25em]">
                <span className="flex items-center gap-2 text-emerald-400"><span className="h-2.5 w-2.5 rounded-full bg-emerald-500 live-dot" />{t("now.onStage")}{d.current.time && <span className="text-slate-400">· {d.current.time}</span>}</span>
                <span data-testid="now-position" className="text-slate-400">{t("now.position")} {d.position} / {d.total}</span>
              </div>
              <div data-testid="now-current-name" className="font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95] mt-4 break-words">{d.current.name}</div>
              <div className="mt-4 text-lg sm:text-xl text-blue-200">{d.current.category} · {d.current.age_group}</div>
              <div className="text-slate-400 mt-1 flex items-center gap-2 flex-wrap">{d.current.school}
                {d.current.is_team && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-300"><Users className="w-3.5 h-3.5" />{t("now.team")}</span>}
              </div>
            </section>
            {d.next.length > 0 && (
              <section className="mt-8">
                <h2 className="font-display text-2xl font-bold uppercase">{t("now.next")}</h2>
                <ol className="mt-3 space-y-2" data-testid="now-next-list">
                  {d.next.map((p, i) => <Row key={p.id} p={p} label={`+${i + 1}`} testid={`now-next-${i + 1}`} highlight={i === 0} />)}
                </ol>
              </section>
            )}
            {d.previous.length > 0 && (
              <section className="mt-8">
                <h2 className="font-display text-xl font-bold uppercase text-slate-400">{t("now.prev")}</h2>
                <ol className="mt-3 space-y-2 opacity-60" data-testid="now-prev-list">
                  {d.previous.map((p) => <Row key={p.id} p={p} label="✓" testid={`now-prev-${p.id}`} />)}
                </ol>
              </section>
            )}
          </>
        )}
      </main>
    </>
  );
}

const BreakCard = ({ d, t }) => (
  <section data-testid="now-break" className="mt-8 fly-card p-6 sm:p-10 border-amber-500/60 shadow-[0_0_50px_rgba(245,158,11,0.2)] rise text-center">
    <Coffee className="w-10 h-10 mx-auto text-amber-300" />
    <div className="font-display font-black uppercase text-6xl sm:text-8xl text-amber-300 mt-3">{t("now.break")}</div>
    {d.up_next && (
      <div data-testid="now-break-next" className="mt-6">
        <div className="font-mono text-xs uppercase tracking-[0.25em] text-slate-400">{t("now.upNext")}</div>
        <div className="font-display text-3xl sm:text-4xl font-bold uppercase mt-2 break-words">{d.up_next.name}</div>
        <div className="text-blue-200 mt-1">{d.up_next.category} · {d.up_next.age_group}</div>
        <div className="text-slate-400 text-sm">{d.up_next.school}</div>
      </div>
    )}
  </section>
);

const Row = ({ p, label, testid, highlight }) => (
  <li data-testid={testid} className={`fly-card px-4 py-3 flex items-center gap-4 ${highlight ? "border-blue-500/60" : ""}`}>
    <span className="font-mono text-xs text-blue-400 w-8 shrink-0">{label}</span>
    <span className="font-mono text-xs text-slate-500 w-12 shrink-0 hidden sm:inline">{p.time}</span>
    <span className="flex-1 min-w-0">
      <span className={`block font-semibold break-words ${highlight ? "text-lg" : ""}`}>{p.name}</span>
      <span className="block text-xs text-slate-400">{p.category} · {p.age_group} · {p.school}</span>
    </span>
  </li>
);
